import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { compose } from 'redux';
import cn from 'classnames';
import get from '../helpers/get';
import formatContentCards from '../helpers/formatContentCards';
import {
  Header1,
  Header2,
  HubHeader,
} from './Header';
import ContentTray from './ContentTray';
import DiscoverSports from './DiscoverSports';
import DonationLabelsHardcoded from './DonationLabelsHardcoded';
import EditionTray from './EditionTray';
import IconicStories from './IconicStories';
import OurBrands from './OurBrands';
import PullQuote from './PullQuote';
import CategorizedTray from './CategorizedTray';
import Tiles from './Tiles';
import SalesBanner from './SalesBanner';
import Seo from './Seo';
import SeoJsonLd from './SeoJsonLd';
import WaitlistBanner from './WaitlistBanner';
import DividingLine from './DividingLine';
import TextDivider from './TextDivider';
import Richtext from './Richtext';
import OurStory from './OurStory';
import Faq from './Faq';
import CompetitionBanner from './CompetitionBanner';
import KeyCalloutBanner from './KeyCalloutBanner';
import { getSortedSlices, getCurrentContentId } from '../selectors';
import theme, { container } from '../styles/theme';
import { getScrollBarWidth } from '../helpers/measure';
import { brazeContentCardCreator } from '../actions/brazeContentCard';
import GreenhouseTray from './GreenhouseTray';
import RecommendationsPlaceholder from './ProductRecommendations';
import SponsoredBrandProducts from './SponsoredBrandProducts';
import DailyFeedPreview from './DailyFeedPreview';
import BrazeBanner from './BrazeBanner';
import SliceType from '../constants/sliceType';

const components = {
  catalog_tray: CategorizedTray,
  content_tray: ContentTray,
  discover_sports: DiscoverSports,
  edition_tray: EditionTray,
  iconic_stories: IconicStories,
  header_1: Header1,
  header_2: Header2,
  hub_header: HubHeader,
  our_brands: OurBrands,
  pull_quote: PullQuote,
  retail_tray: CategorizedTray,
  tiles: Tiles,
  sales_banner: SalesBanner,
  waitlist_banner: WaitlistBanner,
  seo: Seo,
  seo_json_ld: SeoJsonLd,
  dividing_line: DividingLine,
  text_divider: TextDivider,
  text: Richtext,
  our_story: OurStory,
  faq: Faq,
  competition_banner: CompetitionBanner,
  hardcoded_form_giving_made_easy: DonationLabelsHardcoded,
  key_callout_banner: KeyCalloutBanner,
  greenhouse_job_board: GreenhouseTray,
  recommendations_carrousel: RecommendationsPlaceholder,
  [SliceType.SPONSORED_BRAND_PRODUCTS]: SponsoredBrandProducts,
  [SliceType.BRAZE_BANNER]: BrazeBanner,
  preview_deeplink: DailyFeedPreview,
};

const noBottomMargin = content => get(content, ['bottom_margin']) === 'No';
const hasContrastTray = content => get(content, ['contrast_tray']) !== 'No';

const isComponentTightWith = (type, newType) => {
  if ([
    'header_1',
    'header_2',
    'hub_header',
    'sales_banner',
    'waitlist_banner',
    'content_tray',
    'edition_tray',
    'retail_tray',
    'iconic_stories',
  ].includes(type)) {
    return !newType || newType === 'sales_banner';
  }
  return false;
};

const isComponentTightWithPrevious = (type, prevType) => isComponentTightWith(type, prevType);
const isComponentTightWithNext = (type, nextType) => isComponentTightWith(type, nextType);

const isComponentTightWithFooter = (type, content) => {
  const validType = [
    'content_tray',
    'edition_tray',
    'retail_tray',
    'iconic_stories',
  ].includes(type);
  return noBottomMargin(content) || (validType && hasContrastTray(content));
};

const getComponentClassname = (classes, type, content, nextType) => {
  const componentTightWithPrevious = isComponentTightWithPrevious(nextType, type);
  const componentTightWithNext = nextType && isComponentTightWithNext(type, nextType);
  const componentTightWithFooter = !nextType && isComponentTightWithFooter(type, content);

  if (componentTightWithPrevious
    || componentTightWithNext
    || componentTightWithFooter
    || noBottomMargin(content)
  ) {
    return undefined;
  }

  switch (type) {
    case 'sales_banner':
      return classes.salesBannerSpacing;
    case 'seo':
    case 'seo_json_ld':
    case 'text_divider':
      return undefined;
    default:
      return classes.componentSpacing;
  }
};

const genderMapping = {
  men: 'male',
  women: 'female',
  male: 'male',
  female: 'female',
};

const getBrazeContentCards = (dispatch, contentId) => {
  const { appboy, toggles } = global.window;

  if (!toggles['braze-content-card-enabled']
    || !appboy
    || typeof appboy.requestContentCardsRefresh !== 'function'
    || typeof appboy.getCachedContentCards !== 'function') {
    return;
  }

  appboy.requestContentCardsRefresh();
  const filterByGender = card => card.extras.gender === 'all' || card.extras.gender === genderMapping[contentId];
  const contentCards = appboy.getCachedContentCards().cards.filter(filterByGender);
  const formattedContentCards = formatContentCards(contentCards);

  if (formattedContentCards.length > 0) {
    dispatch(brazeContentCardCreator(formattedContentCards));
  }
};

export class AppComponent extends React.Component {
  requestedBannerPlacementIds = [];

  brazeReady = false;

  refreshBanners = slices => {
    const { braze } = global.window;

    if (!braze || typeof braze.requestBannersRefresh !== 'function') {
      return;
    }

    const placementIds = slices.reduce((ids, slice) => {
      const placementId = slice.type === SliceType.BRAZE_BANNER && slice.placement_id;

      if (placementId && ids.indexOf(placementId) === -1) {
        ids.push(placementId);
      }

      return ids;
    }, []).filter(placementId => this.requestedBannerPlacementIds.indexOf(placementId) === -1).slice(0, 10);

    if (placementIds.length > 0) {
      this.requestedBannerPlacementIds = this.requestedBannerPlacementIds.concat(placementIds);
      braze.requestBannersRefresh(placementIds);
    }
  };

  onAppboyReady = () => {
    const { dispatch, contentId, slices } = this.props;
    this.brazeReady = true;
    getBrazeContentCards(dispatch, contentId);
    this.refreshBanners(slices);
  };

  componentDidMount = () => {
    // eslint-disable-next-line no-undef
    const { window } = global;
    const { Event, location } = window;

    if (location.hash) {
      // eslint-disable-next-line no-undef
      const element = document.querySelector(location.hash);

      if (element) {
        element.scrollIntoView();
      }
    }

    window.addEventListener('appboyReady', this.onAppboyReady);
    window.dispatchEvent(new Event('appboyCheck'));
  };

  componentWillUnmount = () => {
    global.window.removeEventListener('appboyReady', this.onAppboyReady);
  };

  componentDidUpdate = prevProps => {
    if (this.brazeReady && prevProps.slices !== this.props.slices) {
      this.refreshBanners(this.props.slices);
    }
  };

  render = () => {
    const {
      classes, slices, contentId, withoutTopPadding,
      withoutBottomPadding,
    } = this.props;

    return (
      <div
        className={cn(
          classes.wrapper,
          withoutTopPadding && classes.withoutTopPadding,
          withoutBottomPadding && classes.withoutBottomPadding,
        )}
      >
        <div className={classes.container}>
          {slices.map(({ sliceId, type, ...content }, i) => {
            const Component = components[type] || null;
            return Component && (
              <Component
                id={contentId}
                key={`${contentId}-${sliceId}`}
                className={getComponentClassname(classes, type, content, get(slices, [i + 1, 'type']))}
                sliceId={sliceId}
                content={content}
              />
            );
          })}
        </div>
      </div>
    );
  };
}

AppComponent.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  slices: PropTypes.arrayOf(PropTypes.object).isRequired,
  withoutTopPadding: PropTypes.bool.isRequired,
  withoutBottomPadding: PropTypes.bool.isRequired,
  contentId: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const slices = getSortedSlices(state);
  const lastSlice = slices[slices.length - 1];
  return {
    contentId: getCurrentContentId(state),
    slices,
    withoutTopPadding: isComponentTightWithPrevious(get(slices, [0, 'type'])),
    withoutBottomPadding: isComponentTightWithFooter(get(lastSlice, ['type']), lastSlice),
  };
};

const insidePageSelector = '#page > #content:first-child > .row:first-child > .columns:first-child > *:first-child &';
export default compose(
  connect(mapStateToProps),

  injectSheet({
    '@global': {
      '#page': {
        // To work around the negative margin calculations below coming out slightly too big
        // in the presence of a vertical scrollbar
        // More info: https://github.com/w3c/csswg-drafts/issues/1766
        overflow: 'hidden',
        '-webkit-font-smoothing': 'antialiased',
      },
    },
    wrapper: {
      // Our components are placed within a `.row > .columns` container, which has a max-width
      // and different padding than we want. So to counteract that we expand the wrapper to fill
      // the viewport and then apply our desired padding and max-width to the inner container
      '.columns &': {
        margin: [0, '-.5rem'],
        [theme.breakpoints.up('xl')]: {
          // negative margins to stretch the wrapper to match the viewport
          margin: [0, `calc(${theme.breakpoints.width('xl') / 2}px - 50vw - .5rem + ${getScrollBarWidth() / 2}px)`],
        },
      },
    },
    withoutTopPadding: {
      [insidePageSelector]: {
        marginTop: '-1rem',
        [theme.breakpoints.up('md')]: {
          marginTop: '-2rem',
        },
      },
    },
    withoutBottomPadding: {
      [insidePageSelector]: {
        marginBottom: '-1rem',
        [theme.breakpoints.up('md')]: {
          marginBottom: '-2rem',
        },
      },
    },
    container,
    componentSpacing: {
      marginBottom: theme.spacing.vertical.sm,
      [theme.breakpoints.up('md')]: {
        marginBottom: theme.spacing.vertical.md,
      },
    },
    salesBannerSpacing: {
      marginBottom: 35,
      [theme.breakpoints.up('md')]: {
        marginBottom: 45,
      },
    },
  }),
)(AppComponent);
